import clsx from 'clsx';
import { useMemo, useState } from 'react';

// Intrinsic pixel sizes are recorded so a height can always be reserved.
// The `transparent` files are generated from the masters by
// scripts/build-brand-assets.js; `standard` is the black-matted master kept as a
// fallback for the onError path below.
const BRAND_ASSETS = {
  darkBg: {
    transparent: '/brand/logo-dark-bg-transparent.png',
    standard: '/brand/logo-dark-bg.png',
    width: 1024,
    height: 724,
  },
  lightBg: {
    // No transparent twin: logo-light-bg.png is dark artwork on a black matte,
    // and dark-on-black cannot be de-matted unambiguously -- solid dark artwork
    // and translucent light artwork are the same pixels. Producing one needs a
    // fresh export from the design source, not a script.
    standard: '/brand/logo-light-bg.png',
    width: 1024,
    height: 724,
  },
  mark: {
    transparent: '/brand/mark-transparent.png',
    standard: '/brand/mark.png',
    width: 512,
    height: 512,
  },
};

// Both variants resolve to the one mark asset, whose artwork is white. It
// therefore needs a dark surface behind it at every call site; a mark for light
// surfaces would need recoloured artwork, not just a different matte.
const MARK_BY_VARIANT = {
  lightBg: 'mark',
  darkBg: 'mark',
};

export default function BrandLogo({
  variant = 'lightBg',
  size = 160,
  showWordmark = true,
  preferTransparent = true,
  alt = 'Genesis Creations',
  className,
}) {
  const [useFallback, setUseFallback] = useState(false);
  const resolvedVariant = showWordmark
    ? variant
    : (MARK_BY_VARIANT[variant] || 'mark');
  const selected = BRAND_ASSETS[resolvedVariant] || BRAND_ASSETS.lightBg;
  const src = useMemo(() => {
    if (!preferTransparent || useFallback || !selected.transparent) return selected.standard;
    return selected.transparent;
  }, [preferTransparent, selected, useFallback]);

  const height = Math.round((size * selected.height) / selected.width);

  return (
    <img
      src={src}
      alt={alt}
      width={size}
      height={height}
      className={clsx('h-auto select-none', className)}
      onError={() => setUseFallback(true)}
      loading="eager"
      decoding="async"
    />
  );
}
